import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { Button, Input, Space, Table, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import { fetchAdminUsers, selectUser } from 'store/slices/adminUsersSlice';
import { IAdminUser } from 'store/slices/types';
import UserDrawer from './UserDrawer';

const { Title } = Typography;

const selectAdminUsers = (state: RootState): RootState['adminUsers'] =>
    state.adminUsers;

export default function UsersListPage(): ReactElement {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { list, loading, selected } = useAppSelector(selectAdminUsers);
    const [search, setSearch] = useState('');

    useEffect(() => {
        dispatch(fetchAdminUsers());
    }, [dispatch]);

    const filtered = useMemo<IAdminUser[]>(() => {
        if (!search.trim()) return list;
        const q = search.trim().toLowerCase();
        const matches = (u: IAdminUser): boolean => {
            const fields = [
                u.first_name,
                u.last_name,
                u.middle_name,
                u.username,
                u.vk_id != null ? String(u.vk_id) : null,
            ];
            return fields.some(
                v => typeof v === 'string' && v.toLowerCase().includes(q)
            );
        };
        return list.filter(matches);
    }, [list, search]);

    return (
        <div>
            <Title level={3}>Участники</Title>
            <Space style={{ marginBottom: 16 }}>
                <Input.Search
                    placeholder="Поиск по имени / username / vk_id"
                    value={search}
                    onChange={(e): void => setSearch(e.target.value)}
                    style={{ width: 360 }}
                    allowClear
                />
                <Button
                    type="primary"
                    onClick={(): void => navigate('/admin/users/bulk')}
                >
                    Массовая загрузка
                </Button>
            </Space>
            <Table<IAdminUser>
                rowKey="id"
                loading={loading}
                dataSource={filtered}
                onRow={(u): object => ({
                    onClick: (): void => {
                        dispatch(selectUser(u));
                    },
                    style: { cursor: 'pointer' },
                })}
                columns={[
                    { title: 'ID', dataIndex: 'id', width: 80 },
                    {
                        title: 'ФИО',
                        render: (_: unknown, u: IAdminUser): string =>
                            [u.last_name, u.first_name, u.middle_name]
                                .filter(Boolean)
                                .join(' '),
                    },
                    { title: 'VK', dataIndex: 'vk_id', width: 120 },
                    { title: 'Username', dataIndex: 'username', width: 160 },
                    { title: 'Event', dataIndex: 'event_id', width: 100 },
                ]}
                pagination={{ pageSize: 50 }}
            />
            <UserDrawer
                user={selected}
                onClose={(): void => {
                    dispatch(selectUser(null));
                }}
            />
        </div>
    );
}
